import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { UserPlus, Ticket, Send } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/36095035/pexels-photo-36095035.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WEDDING_IMG = "https://images.unsplash.com/photo-1767986012547-3fc29b18339f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwd2VkZGluZyUyMGNlbGVicmF0aW9ufGVufDB8fHx8MTc3Njc4OTI3NXww&ixlib=rb-4.1.0&q=85";
const DINNER_IMG = "https://images.unsplash.com/photo-1768508947591-b650dacc70a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxzb3BoaXN0aWNhdGVkJTIwZGlubmVyJTIwcGFydHl8ZW58MHx8fHwxNzc2Nzg5Mjc1fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [etsyUrl, setEtsyUrl] = useState("");
  useEffect(() => {
    api.get("/settings").then((r) => setEtsyUrl(r.data?.etsy_url || "")).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#FBF9F4] text-neutral-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#FBF9F4]/70" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-24 grid lg:grid-cols-12 gap-10 items-end">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="lg:col-span-8">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-600 mb-6">Vol. I · Issue 01 · Invitations & RSVPs</div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tight font-light">
              The invitation,<br/>
              <em className="italic font-normal">thoughtfully</em> reimagined.
            </h1>
            <p className="mt-8 max-w-xl text-lg text-neutral-700 leading-relaxed">
              Craft online invites that feel like heirlooms. A live countdown, a map, a custom RSVP — all in one elegant link.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register"><Button data-testid="landing-start" className="rounded-sm bg-neutral-900 hover:bg-neutral-800 px-8 h-12 text-sm tracking-[0.2em] uppercase">Start a Soirée</Button></Link>
              <a href="#how-it-works"><Button variant="outline" data-testid="landing-how" className="rounded-sm border-neutral-900 h-12 px-8 text-sm tracking-[0.2em] uppercase">How it works</Button></a>
              <Link to="/login"><Button variant="ghost" data-testid="landing-login" className="rounded-sm h-12 px-4 text-sm tracking-[0.2em] uppercase">I have an account</Button></Link>
            </div>
          </motion.div>
          <div className="lg:col-span-4 hidden lg:block">
            <div className="text-xs tracking-[0.25em] uppercase text-neutral-500 border-l border-black/20 pl-4">
              A studio for hosts who believe arrival begins with the invitation.
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-7 border border-black/10 bg-white p-10">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">01 · Designed together</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl sm:text-5xl tracking-tight mt-4 font-normal leading-tight">
              Pick a type. Pick a mood. Pick a palette.
            </h2>
            <p className="text-neutral-600 mt-4 max-w-lg">
              Nine event archetypes — from weddings to housewarmings — each with bespoke themes. Feeling indecisive? Hit <em>Surprise Me</em>.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5 border border-black/10 overflow-hidden relative min-h-[280px]">
            <img src={WEDDING_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative p-8 text-white">
              <div className="text-[10px] tracking-[0.3em] uppercase">Theme · Romantic Garden</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl mt-2 font-light">Wedding</div>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4 border border-black/10 bg-white p-8">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">02 · A living page</div>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl mt-3 font-medium">Countdown & Maps</h3>
            <p className="text-neutral-600 mt-2 text-sm leading-relaxed">Seconds tick on the public invite. Guests open the map and get directions with one tap.</p>
          </div>
          <div className="col-span-12 lg:col-span-4 border border-black/10 bg-white p-8">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">03 · RSVP, your way</div>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl mt-3 font-medium">Custom questions</h3>
            <p className="text-neutral-600 mt-2 text-sm leading-relaxed">Add free text, dropdowns, and checkbox questions. Every detail, exactly as you need it.</p>
          </div>
          <div className="col-span-12 lg:col-span-4 border border-black/10 overflow-hidden relative min-h-[220px]">
            <img src={DINNER_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/30" />
            <div className="relative p-8 text-white h-full flex flex-col justify-end">
              <div className="text-[10px] tracking-[0.3em] uppercase">04 · Shareable</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl mt-1 font-light">Link + QR</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-black/10">
        <div className="max-w-2xl">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">How it works</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl sm:text-5xl tracking-tight mt-4 font-normal leading-tight">
            Three unhurried steps.
          </h2>
          <p className="text-neutral-600 mt-4">
            Building and previewing your invitation is always free. When you&apos;re ready to share it with your guests, one redemption code on Etsy unlocks it.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6 mt-12">
          <div className="col-span-12 md:col-span-4 border border-black/10 bg-white p-8">
            <div className="w-10 h-10 border border-neutral-900 flex items-center justify-center">
              <UserPlus size={18} />
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mt-6">Step 01</div>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl mt-2 font-normal">Create your studio</h3>
            <p className="text-neutral-600 mt-3 text-sm leading-relaxed">
              Sign up in seconds. No payment details needed. Design, preview and tweak as many invites as you like.
            </p>
          </div>

          <div className="col-span-12 md:col-span-4 border border-black/10 bg-white p-8">
            <div className="w-10 h-10 border border-neutral-900 flex items-center justify-center">
              <Ticket size={18} />
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mt-6">Step 02</div>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl mt-2 font-normal">Buy a code on Etsy</h3>
            <p className="text-neutral-600 mt-3 text-sm leading-relaxed">
              Each Soirée redemption code unlocks one invitation — shareable link, live countdown, RSVP tracking, the lot.
            </p>
            {etsyUrl ? (
              <a href={etsyUrl} target="_blank" rel="noreferrer" data-testid="how-etsy-link">
                <Button className="mt-5 rounded-sm bg-neutral-900 hover:bg-neutral-800 tracking-[0.2em] uppercase text-xs h-11">Get a code on Etsy ↗</Button>
              </a>
            ) : (
              <div className="mt-5 text-xs tracking-[0.25em] uppercase text-neutral-500">Etsy shop link coming soon</div>
            )}
          </div>

          <div className="col-span-12 md:col-span-4 border border-black/10 bg-white p-8">
            <div className="w-10 h-10 border border-neutral-900 flex items-center justify-center">
              <Send size={18} />
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mt-6">Step 03</div>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-3xl mt-2 font-normal">Publish & share</h3>
            <p className="text-neutral-600 mt-3 text-sm leading-relaxed">
              Paste your code inside Soirée. Your invitation goes live with a shareable link and QR — then watch the RSVPs roll in.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-black/10 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap justify-between items-center gap-4 text-xs tracking-[0.2em] uppercase text-neutral-500">
          <div>© Soirée · Made with intent</div>
          <div className="flex gap-6">
            <a href="/legal/privacy" data-testid="footer-privacy" className="hover:text-neutral-900">Privacy</a>
            <a href="/legal/terms" data-testid="footer-terms" className="hover:text-neutral-900">Terms</a>
            <a href="mailto:admin@soiree-invites.com" className="hover:text-neutral-900">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
